/**
 * Variant Manager Component
 *
 * Comprehensive variant management for inventory items with pricing,
 * stock levels, and detailed variant tracking
 */

import {
  CurrencyDollarIcon,
  PackageIcon,
  PlusIcon,
  TagIcon,
} from '@phosphor-icons/react';
import { useMemo, useState } from 'react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DataTable } from '@/components/ui/data-table';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { useEnhancedApi } from '@/hooks/use-enhanced-api';
import { useToast } from '@/hooks/use-toast';
import { ActionConfig, DataTableColumn } from '@/types/datatable';
import {
  InventoryItem,
  InventoryVariant,
  QualityInfo,
} from '@/types/inventory';
import { formatCurrency, formatNumber } from '@/utils';

interface VariantManagerProps {
  inventoryItem: InventoryItem;
  onVariantUpdate?: () => void;
  className?: string;
}

interface VariantFormData {
  name: string;
  quantity: number;
  price: number;
  reorderLevel: number;
  sku: string;
  barcode: string;
  qualityGrade: QualityInfo['grade'];
}

const QUALITY_GRADES: Array<{
  value: QualityInfo['grade'];
  label: string;
  color: string;
}> = [
  {
    value: 'Premium',
    label: 'Premium',
    color: 'bg-purple-100 text-purple-800',
  },
  { value: 'A', label: 'Grade A', color: 'bg-green-100 text-green-800' },
  { value: 'B', label: 'Grade B', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'C', label: 'Grade C', color: 'bg-orange-100 text-orange-800' },
  { value: 'Rejected', label: 'Rejected', color: 'bg-red-100 text-red-800' },
];

const VARIANT_SIZES = [
  'Extra Small',
  'Small',
  'Medium',
  'Large',
  'Extra Large',
  '100g',
  '250g',
  '500g',
  '1kg',
  '2kg',
  '5kg',
];

export const VariantManager: React.FC<VariantManagerProps> = ({
  inventoryItem,
  onVariantUpdate,
  className = '',
}) => {
  const { inventory: inventoryApi } = useEnhancedApi();
  const { toast } = useToast();
  const showToast = (message: string, isSuccess: boolean) => {
    toast({
      title: isSuccess ? 'Success' : 'Error',
      description: message,
      variant: isSuccess ? 'default' : 'destructive',
    });
  };
  const [variants, setVariants] = useState<InventoryVariant[]>(
    inventoryItem.variant || []
  );
  const [loading, setLoading] = useState(false);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [editingVariant, setEditingVariant] = useState<InventoryVariant | null>(
    null
  );

  // Form state
  const [formData, setFormData] = useState<VariantFormData>({
    name: '',
    quantity: 0,
    price: inventoryItem.price || 0,
    reorderLevel: inventoryItem.reorderLevel || 10,
    sku: '',
    barcode: '',
    qualityGrade: 'A',
  });

  // Calculate variant statistics
  const variantStats = useMemo(() => {
    const totalQuantity = variants.reduce(
      (sum, variant) => sum + variant.quantity,
      0
    );
    const totalValue = variants.reduce(
      (sum, variant) => sum + variant.quantity * variant.price,
      0
    );
    const lowStockVariants = variants.filter(
      variant => variant.quantity <= variant.reorderLevel
    ).length;
    const outOfStockVariants = variants.filter(
      variant => variant.quantity === 0
    ).length;
    const averagePrice =
      variants.length > 0
        ? variants.reduce((sum, variant) => sum + variant.price, 0) /
          variants.length
        : 0;

    return {
      totalVariants: variants.length,
      totalQuantity,
      totalValue,
      lowStockVariants,
      outOfStockVariants,
      averagePrice,
    };
  }, [variants]);

  // Table columns
  const variantColumns: DataTableColumn<InventoryVariant>[] = [
    {
      id: 'name',
      header: 'Variant Name',
      accessor: 'name',
      cell: ({ row }) => (
        <div className='font-medium'>
          <div className='flex items-center gap-2'>
            <TagIcon className='h-4 w-4 text-blue-500' />
            {(row as any).name}
          </div>
          {(row as any).sku && (
            <div className='text-xs text-gray-500 mt-1'>
              SKU: {(row as any).sku}
            </div>
          )}
        </div>
      ),
    },
    {
      id: 'quantity',
      header: 'Stock',
      accessor: 'quantity',
      cell: ({ row }) => {
        const r: any = row as any;
        const isLowStock = r.quantity <= r.reorderLevel;
        const isOutOfStock = r.quantity === 0;

        return (
          <div className='flex items-center gap-2'>
            <PackageIcon
              className={`h-4 w-4 ${
                isOutOfStock
                  ? 'text-red-500'
                  : isLowStock
                    ? 'text-orange-500'
                    : 'text-green-500'
              }`}
            />
            <div>
              <span
                className={`font-medium ${
                  isOutOfStock
                    ? 'text-red-600'
                    : isLowStock
                      ? 'text-orange-600'
                      : ''
                }`}
              >
                {formatNumber((row as any).quantity)}{' '}
                {String(inventoryItem.measurementUnit) || 'units'}
              </span>
              <div className='text-xs text-gray-500'>
                Reorder at: {(row as any).reorderLevel}
              </div>
            </div>
          </div>
        );
      },
    },
    {
      id: 'price',
      header: 'Price',
      accessor: 'price',
      cell: ({ row }) => (
        <div className='flex items-center gap-2'>
          <CurrencyDollarIcon className='h-4 w-4 text-green-500' />
          <span className='font-medium'>
            {formatCurrency((row as any).price)}
          </span>
        </div>
      ),
    },
    {
      id: 'value',
      header: 'Total Value',
      accessor: 'price',
      cell: ({ row }) => (
        <span className='font-medium'>
          {formatCurrency((row as any).quantity * (row as any).price)}
        </span>
      ),
    },
    {
      id: 'qualityGrade',
      header: 'Quality',
      accessor: 'qualityGrade',
      cell: ({ row }) => {
        const r: any = row as any;
        if (!r.qualityGrade) return null;
        const grade = QUALITY_GRADES.find(g => g.value === r.qualityGrade);
        return (
          <Badge className={grade?.color || 'bg-gray-100 text-gray-800'}>
            {grade?.label || (row as any).qualityGrade}
          </Badge>
        );
      },
    },
    {
      id: 'barcode',
      header: 'Barcode',
      accessor: 'barcode',
      cell: ({ row }) => (
        <span className='text-sm font-mono text-gray-600'>
          {(row as any).barcode || '-'}
        </span>
      ),
    },
  ];

  // Reset form
  const resetForm = () => {
    setFormData({
      name: '',
      quantity: 0,
      price: inventoryItem.price || 0,
      reorderLevel: inventoryItem.reorderLevel || 10,
      sku: '',
      barcode: '',
      qualityGrade: 'A',
    });
    setEditingVariant(null);
  };

  // Generate SKU
  const generateSKU = () => {
    const baseCode = inventoryItem.sku || inventoryItem.internalCode || 'ITM';
    const variantCount = variants.length + 1;
    const generatedSKU = `${baseCode}-V${variantCount.toString().padStart(2, '0')}`;
    setFormData(prev => ({ ...prev, sku: generatedSKU }));
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const variantData = {
        ...formData,
        _id: editingVariant?._id,
      };

      if (editingVariant) {
        // Update existing variant
        await inventoryApi.updateVariant(
          inventoryItem._id!,
          editingVariant._id!,
          variantData
        );
        const updatedVariants = variants.map(variant =>
          variant._id === editingVariant._id
            ? { ...variantData, _id: editingVariant._id }
            : variant
        );
        setVariants(updatedVariants);
        showToast('Variant updated successfully', true);
      } else {
        // Add new variant
        await inventoryApi.addVariant(inventoryItem._id!, variantData);
        const newVariant: InventoryVariant = {
          ...variantData,
          _id: `temp-${Date.now()}`, // Temporary ID until backend provides real one
        };
        setVariants(prev => [...prev, newVariant]);
        showToast('Variant added successfully', true);
      }

      resetForm();
      setIsSheetOpen(false);
      onVariantUpdate?.();
    } catch (error) {
      showToast('Failed to save variant', false);
    } finally {
      setLoading(false);
    }
  };

  // Handle edit variant
  const handleEditVariant = (variant: InventoryVariant) => {
    setFormData({
      name: variant.name,
      quantity: variant.quantity,
      price: variant.price,
      reorderLevel: variant.reorderLevel,
      sku: variant.sku || '',
      barcode: variant.barcode || '',
      qualityGrade: variant.qualityGrade || 'A',
    });
    setEditingVariant(variant);
    setIsSheetOpen(true);
  };

  // Handle delete variant
  const handleDeleteVariant = async (variant: InventoryVariant) => {
    if (
      !confirm(
        `Are you sure you want to delete the variant "${variant.name}"? This action cannot be undone.`
      )
    ) {
      return;
    }

    try {
      if (variant._id && !variant._id.startsWith('temp-')) {
        await inventoryApi.deleteVariant(inventoryItem._id!, variant._id);
      }
      setVariants(prev => prev.filter(v => v._id !== variant._id));
      showToast('Variant deleted successfully', true);
      onVariantUpdate?.();
    } catch (error) {
      showToast('Failed to delete variant', false);
    }
  };

  // Row actions
  const variantRowActions: ActionConfig<InventoryVariant>[] = [
    {
      key: 'edit',
      label: 'Edit Variant',
      onClick: (row: InventoryVariant) => handleEditVariant(row),
    },
    {
      key: 'delete',
      label: 'Delete Variant',
      onClick: (row: InventoryVariant) => handleDeleteVariant(row),
      variant: 'destructive',
    },
  ];

  if (!inventoryItem.isVariant) {
    return (
      <Alert>
        <AlertDescription>
          This inventory item is not configured for variants. Enable variant
          management in the product settings to use this feature.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Variant Statistics */}
      <div className='grid grid-cols-1 md:grid-cols-6 gap-4'>
        <Card>
          <CardContent className='p-4'>
            <div className='text-sm font-medium text-gray-500'>
              Total Variants
            </div>
            <div className='text-2xl font-bold'>
              {variantStats.totalVariants}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className='p-4'>
            <div className='text-sm font-medium text-gray-500'>Total Stock</div>
            <div className='text-2xl font-bold text-blue-600'>
              {formatNumber(variantStats.totalQuantity)}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className='p-4'>
            <div className='text-sm font-medium text-gray-500'>Total Value</div>
            <div className='text-2xl font-bold text-green-600'>
              {formatCurrency(variantStats.totalValue)}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className='p-4'>
            <div className='text-sm font-medium text-gray-500'>Avg Price</div>
            <div className='text-2xl font-bold'>
              {formatCurrency(variantStats.averagePrice)}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className='p-4'>
            <div className='text-sm font-medium text-gray-500'>Low Stock</div>
            <div className='text-2xl font-bold text-orange-600'>
              {variantStats.lowStockVariants}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className='p-4'>
            <div className='text-sm font-medium text-gray-500'>
              Out of Stock
            </div>
            <div className='text-2xl font-bold text-red-600'>
              {variantStats.outOfStockVariants}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Variant Management */}
      <Card>
        <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-4'>
          <CardTitle className='text-lg font-semibold'>
            Product Variants
          </CardTitle>
          <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
            <SheetTrigger asChild>
              <Button onClick={resetForm}>
                <PlusIcon className='h-4 w-4 mr-2' />
                Add Variant
              </Button>
            </SheetTrigger>
            <SheetContent className='w-full sm:max-w-lg'>
              <SheetHeader>
                <SheetTitle>
                  {editingVariant ? 'Edit Variant' : 'Add New Variant'}
                </SheetTitle>
              </SheetHeader>

              <form onSubmit={handleSubmit} className='space-y-4 mt-6'>
                <div className='space-y-2'>
                  <Label htmlFor='variantName'>Variant Name *</Label>
                  <div className='flex gap-2'>
                    <Select
                      value={formData.name}
                      onValueChange={value =>
                        setFormData(prev => ({ ...prev, name: value }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder='Select size...' />
                      </SelectTrigger>
                      <SelectContent>
                        {VARIANT_SIZES.map(size => (
                          <SelectItem key={size} value={size}>
                            {size}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      value={formData.name}
                      onChange={e =>
                        setFormData(prev => ({ ...prev, name: e.target.value }))
                      }
                      placeholder='Or enter custom name'
                    />
                  </div>
                </div>

                <div className='grid grid-cols-2 gap-4'>
                  <div className='space-y-2'>
                    <Label htmlFor='quantity'>Stock Quantity *</Label>
                    <Input
                      id='quantity'
                      type='number'
                      value={formData.quantity}
                      onChange={e =>
                        setFormData(prev => ({
                          ...prev,
                          quantity: parseFloat(e.target.value) || 0,
                        }))
                      }
                      placeholder='0'
                      min='0'
                      step='0.01'
                      required
                    />
                  </div>
                  <div className='space-y-2'>
                    <Label htmlFor='reorderLevel'>Reorder Level *</Label>
                    <Input
                      id='reorderLevel'
                      type='number'
                      value={formData.reorderLevel}
                      onChange={e =>
                        setFormData(prev => ({
                          ...prev,
                          reorderLevel: parseFloat(e.target.value) || 0,
                        }))
                      }
                      placeholder='10'
                      min='0'
                      required
                    />
                  </div>
                </div>

                <div className='space-y-2'>
                  <Label htmlFor='price'>Price *</Label>
                  <Input
                    id='price'
                    type='number'
                    value={formData.price}
                    onChange={e =>
                      setFormData(prev => ({
                        ...prev,
                        price: parseFloat(e.target.value) || 0,
                      }))
                    }
                    placeholder='0.00'
                    min='0'
                    step='0.01'
                    required
                  />
                </div>

                <div className='space-y-2'>
                  <div className='flex items-center justify-between'>
                    <Label htmlFor='sku'>SKU</Label>
                    <Button
                      type='button'
                      hierarchy='outlined'
                      size='sm'
                      onClick={generateSKU}
                    >
                      Generate
                    </Button>
                  </div>
                  <Input
                    id='sku'
                    value={formData.sku}
                    onChange={e =>
                      setFormData(prev => ({ ...prev, sku: e.target.value }))
                    }
                    placeholder='Auto-generated or custom SKU'
                  />
                </div>

                <div className='space-y-2'>
                  <Label htmlFor='barcode'>Barcode</Label>
                  <Input
                    id='barcode'
                    value={formData.barcode}
                    onChange={e =>
                      setFormData(prev => ({
                        ...prev,
                        barcode: e.target.value,
                      }))
                    }
                    placeholder='Product barcode'
                  />
                </div>

                <div className='space-y-2'>
                  <Label htmlFor='qualityGrade'>Quality Grade</Label>
                  <Select
                    value={formData.qualityGrade}
                    onValueChange={value =>
                      setFormData(prev => ({
                        ...prev,
                        qualityGrade: value as QualityInfo['grade'],
                      }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {QUALITY_GRADES.map(grade => (
                        <SelectItem key={grade.value} value={grade.value}>
                          {grade.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <Separator />

                <div className='flex justify-end gap-2'>
                  <Button
                    type='button'
                    hierarchy='outlined'
                    onClick={() => setIsSheetOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button type='submit' loading={loading}>
                    {editingVariant ? 'Update Variant' : 'Add Variant'}
                  </Button>
                </div>
              </form>
            </SheetContent>
          </Sheet>
        </CardHeader>
        <CardContent>
          <DataTable
            data={variants}
            columns={variantColumns}
            rowActions={variantRowActions}
            emptyConfig={{
              title: 'No Variants Found',
              description:
                'Add product variants to track different sizes, colors, or specifications.',
            }}
          />
        </CardContent>
      </Card>
    </div>
  );
};
